/**
	* Zoom bar view
	* Handles: zoom bar
	* @module ZoomBar
*/

/// Imports

// React
import { useState, useEffect } from "react";

// jQuery
import $ from "jquery";

// OpenLayers
import OlMap from "ol/Map";
import View from "ol/View";

// Models
import Vector2 from "lib/classes/vector2";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Tools
import { reverseNormal, roundtodp } from "lib/utilities/maths";

// Styles
import "styles/css/ZoomBar.scss";

interface ZoomBarProps {
	olMap:OlMap,
	zoomLevel:number
}

var ready:boolean = false;
var draggingSlider:boolean = false;
var dragMousePos:Vector2;

export default function ZoomBar({olMap, zoomLevel}:ZoomBarProps) {
	
	// Initialize after first draw
	useEffect(() => {
		const firstAdjust = () => {
			ready = true;
			adjust();
		}
		// Relies on #base-map-switcher being available
		setTimeout(() => firstAdjust(), 100);
		
		window.addEventListener("mousemove", onKnobDrag);
		window.addEventListener("touchmove", onKnobDrag);
		
		// Guarantee the mouse up event needed for the knob is called
		window.addEventListener("mouseup", onKnobMouseUp);
		window.addEventListener("touchend", onKnobMouseUp);
	}, []);
	
	// Adjust position and height of zoom bar when screen dimensions change
	useEffect(() => {
		adjust();
	}, [_dimensions]);
	
	// Adjust knob when zoom level changes
	useEffect(() => {
		if(!draggingSlider)
			adjustKnob();
	}, [zoomLevel]);
	
	const adjust = () => {
		if(!ready) return;
		
		let height:number = 500;
		const padding:number = 150;
		
		if(_dimensions.height < height+padding) {
			height -= height+padding - _dimensions.height;
		}
		
		$("#zoom-bar").fadeIn(_ui.transitionUpOptions);
		
		if(!_ui.tooSmallForZoomBar())
			$("#zoom-bar").css("height", height);
		else
			$("#zoom-bar").css("height", "auto");
		
		adjustKnob();
	}
	
	const adjustKnob = () => {
		const view:View = olMap.getView();
		const zoom:number = view.getResolutions() ? view.getZoomForResolution(view.getResolution()) : view.getZoom();
		const minZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMaxResolution()) : view.getMinZoom();
		const maxZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMinResolution()) : view.getMaxZoom();
		
		const zoomPcnt:number = reverseNormal((zoom - minZoom) / (maxZoom - minZoom)) * 100;
		
		$("#zoom-bar-knob").css("transform", `translateY(${zoomPcnt}%)`);
	}
	
	const getMousePos = (e:any):Vector2 => {
		if(!e.touches)
			return new Vector2(e.clientX, e.clientY);
		else
			return new Vector2(e.touches[0].clientX, e.touches[0].clientY);
	}
	const adjustZoom = (zoom:number, animate:boolean = false, absolute:boolean = false) => {
		const view:View = olMap.getView();
		
		if(animate && view.getConstrainResolution()) {
			zoom = Math.round(zoom);
		}
		
		if(!animate)
			!absolute ? view.adjustZoom(zoom) : view.setZoom(zoom);
		else
			view.animate({zoom: (!absolute ? view.getZoom() : 0) + zoom, duration: 100});
			
		// Adjust the knob after a zoom
		adjustKnob();
	}
	
	const onKnobMouseDown = (e:any) => {
		e.preventDefault();
		
		draggingSlider = true;
	}
	const onKnobMouseUp = (e:any) => {
		// window onMouseUp event may be called when slider not dragging
		if(!draggingSlider) return;
		
		e.preventDefault();
		
		draggingSlider = false;
	}
	const onKnobDrag = (e:any) => {
		if(!draggingSlider) return;
		
		const view:View = olMap.getView();
		const minZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMaxResolution()) : view.getMinZoom();
		const maxZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMinResolution()) : view.getMaxZoom();
		const rect:DOMRect = $("#zoom-scale-trigger").get(0).getBoundingClientRect();
		const y:number = (e.clientY - rect.top) - 10;
		const zoomPcnt:number = reverseNormal(y / $("#zoom-bar-knob").height());
		const zoomSet:number = ((maxZoom-minZoom) * zoomPcnt) + minZoom;
		
		adjustZoom(zoomSet, false, true);
	}
	const onScaleClick = (e:any) => {
		const view:View = olMap.getView();
		const minZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMaxResolution()) : view.getMinZoom();
		const maxZoom:number = view.getResolutions() ? view.getZoomForResolution(view.getMinResolution()) : view.getMaxZoom();
		const rect:DOMRect = e.target.getBoundingClientRect();
		const y:number = (e.clientY - rect.top) - 10;
		const zoomPcnt:number = reverseNormal(y / $("#zoom-bar-knob").height());
		const zoomSet:number = ((maxZoom-minZoom) * zoomPcnt) + minZoom;
		
		adjustZoom(zoomSet, true, true);
	}
	
	/*
		Draw zoom bar
	*/
	const drawZoomBar = () => {
		const zoomPcnt:number = 50;
		
		const drawZoomScale = () => {
			if(_ui.tooSmallForZoomBar()) return null;
			return (
				<div className="zoom-bar__scale prevent-select">
					<div id="zoom-scale-trigger" className="zoom-bar__scale-trigger" onClick={onScaleClick}></div>
					<div id="zoom-bar-knob" className="zoom-bar__knob-container prevent-select">
						<div 
							className="zoom-bar__knob"
							onTouchStart={onKnobMouseDown}
							onMouseDown={onKnobMouseDown}
							onMouseUp={onKnobMouseUp}
						></div>
					</div>
				</div>
			)
		}
		
		let classTop:string = "zoom-bar__button zoom-bar__button--top prevent-select";
		let classBottom:string = "zoom-bar__button zoom-bar__button--bottom prevent-select";
		
		if(_ui.tooSmallForZoomBar()) {
			classTop += " zoom-bar__button--mini";
			classBottom += " zoom-bar__button--mini";
		}
		
		return (
			<div id="zoom-bar" className="rect zoom-bar prevent-select" onClick={(e:any) => e.preventDefault()}>
				<div className={classTop} onClick={() => adjustZoom(1, true)}>
					+
				</div>
				{drawZoomScale()}
				<div className={classBottom} onClick={() => adjustZoom(-1, true)}>
					-
				</div>
			</div>
		)
	}
	
	return drawZoomBar();
}